/**
 * Business-day arithmetic for the roadmap screens.
 *
 * CLONED from the helpers `views/roadmap-execution/finalization.ejs` and `definition.ejs` both
 * carry inline — `pd`, `fmtY`, `isNonWorking`, `ensureBiz`, `addBizInclusive`, `nextBiz`,
 * `businessDays`. One copy here rather than two, because the two source views carry byte-
 * identical definitions and a second copy is a second thing to keep in step.
 *
 * ── LOCAL DATES, NOT UTC, AND THAT IS DELIBERATE ────────────────────────────────────────
 *
 * The source parses `YYYY-MM-DD` into a LOCAL midnight (`new Date(y, m-1, d)`), and this does
 * the same. A phase boundary is a calendar date the user typed, not an instant: parsing it as
 * UTC and formatting it back in local time shifts it a day for anyone west of Greenwich, which
 * is how a Monday start silently becomes a Sunday. The Gantt bars are a different case — those
 * are compared as instants, so `RoadmapDefinition.tsx` parses them as UTC on purpose.
 */

/** Weekend, or a listed holiday. */
export function isNonWorking(d: Date): boolean {
  const w = d.getDay();
  if (w === 0 || w === 6) return true;
  return holidaySet(d.getFullYear()).has(ymd(d));
}

/** `YYYY-MM-DD` for a local Date. */
export function fmtY(d: Date): string {
  return (
    d.getFullYear() +
    '-' +
    String(d.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(d.getDate()).padStart(2, '0')
  );
}
const ymd = fmtY;

/** Parse `YYYY-MM-DD` to local midnight. Null for anything unparseable. */
export function pd(str: unknown): Date | null {
  if (!str) return null;
  const p = String(str).split('T')[0]!.split('-').map(Number);
  if (!p[0]) return null;
  return new Date(p[0], (p[1] ?? 1) - 1, p[2] ?? 1);
}

/**
 * US federal holidays, computed per year — the source's own `holidaySet`.
 *
 * Computed rather than listed so it does not expire: a hardcoded table is correct until the
 * year it is not, and nothing announces that.
 */
const holidayCache = new Map<number, Set<string>>();
export function holidaySet(year: number): Set<string> {
  const cached = holidayCache.get(year);
  if (cached) return cached;
  const out = new Set<string>();
  /** The nth given weekday of a month; n = -1 means the last one. */
  const nth = (month: number, weekday: number, n: number): Date => {
    if (n > 0) {
      const d = new Date(year, month, 1);
      let count = 0;
      while (true) {
        if (d.getDay() === weekday) {
          count++;
          if (count === n) return new Date(d);
        }
        d.setDate(d.getDate() + 1);
      }
    }
    const d = new Date(year, month + 1, 0);
    while (d.getDay() !== weekday) d.setDate(d.getDate() - 1);
    return d;
  };
  /** A fixed date, shifted off a weekend the way the observed holiday is. */
  const fixed = (month: number, day: number): Date => {
    const d = new Date(year, month, day);
    if (d.getDay() === 6) d.setDate(d.getDate() - 1);
    else if (d.getDay() === 0) d.setDate(d.getDate() + 1);
    return d;
  };
  [
    fixed(0, 1),          // New Year's Day
    nth(0, 1, 3),         // MLK — 3rd Monday of January
    nth(1, 1, 3),         // Presidents' Day — 3rd Monday of February
    nth(4, 1, -1),        // Memorial Day — last Monday of May
    fixed(5, 19),         // Juneteenth
    fixed(6, 4),          // Independence Day
    nth(8, 1, 1),         // Labor Day — 1st Monday of September
    nth(10, 4, 4),        // Thanksgiving — 4th Thursday of November
    fixed(11, 25),        // Christmas Day
  ].forEach((d) => out.add(ymd(d)));
  holidayCache.set(year, out);
  return out;
}

/** The date itself if it is a working day, otherwise the next one that is. */
export function ensureBiz(d: Date): Date {
  const c = new Date(d);
  while (isNonWorking(c)) c.setDate(c.getDate() + 1);
  return c;
}

/**
 * The date `w` WORKING DAYS from `start`, counting the start itself as day 1.
 *
 * Inclusive, which is why a one-day phase ends on the day it starts.
 */
export function addBizInclusive(start: Date, w: number): Date {
  const c = ensureBiz(start);
  let count = 1;
  while (count < w) {
    c.setDate(c.getDate() + 1);
    if (!isNonWorking(c)) count++;
  }
  return c;
}

/** The next working day strictly after `d`. */
export function nextBiz(d: Date): Date {
  const c = new Date(d);
  c.setDate(c.getDate() + 1);
  return ensureBiz(c);
}

/** Working days between two dates, inclusive of both. Never less than 1. */
export function businessDays(s: unknown, e: unknown): number {
  const a = pd(s);
  const b = pd(e);
  if (!a || !b) return 1;
  let n = 0;
  const cur = new Date(a);
  while (cur <= b) {
    if (!isNonWorking(cur)) n++;
    cur.setDate(cur.getDate() + 1);
  }
  return n || 1;
}
